import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  useCompleteRohsProductComplianceFile,
  useUncompleteRohsProductComplianceFile,
  type RohsProductComplianceFileView,
} from '@/shared/hooks/api/rohsComplianceFiles'
import { useComplianceActorDisplayLabel } from '@/shared/hooks/useComplianceActorDisplayLabel'

function formatCompletedAt(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso))
}

export function RohsProductComplianceFileSignOffActions({
  productId,
  productFile,
}: {
  productId: string
  productFile: RohsProductComplianceFileView
}) {
  const { t, i18n } = useTranslation('common')
  const { mutateAsync: completeFile, isPending: isCompleting } =
    useCompleteRohsProductComplianceFile(productId)
  const { mutateAsync: uncompleteFile, isPending: isUncompleting } =
    useUncompleteRohsProductComplianceFile(productId)

  const isPending = isCompleting || isUncompleting
  const isComplete = productFile.completedAt != null
  const actorLabel = useComplianceActorDisplayLabel(productFile.completedByActor)

  const handleComplete = async () => {
    try {
      const result = await completeFile()
      if (result.warnings.includes('MANUFACTURER_EVIDENCE_INCOMPLETE')) {
        toast.warning(
          t(
            'v2.pages.products.detail.rohsFiles.warnings.manufacturerEvidenceIncomplete'
          )
        )
      }
    } catch {
      toast.error(t('v2.pages.products.detail.rohsFiles.actions.completeFailed'))
    }
  }

  const handleUncomplete = async () => {
    try {
      await uncompleteFile()
    } catch {
      toast.error(
        t('v2.pages.products.detail.rohsFiles.actions.uncompleteFailed')
      )
    }
  }

  return (
    <div className='flex flex-wrap items-center justify-end gap-2'>
      {isComplete &&
      productFile.completedAt != null &&
      productFile.completedByActor != null ? (
        <span className='text-xs text-muted-foreground'>
          {t('v2.pages.products.detail.rohsFiles.completedAt', {
            date: formatCompletedAt(productFile.completedAt, i18n.language),
            actor: actorLabel,
          })}
        </span>
      ) : null}
      {isComplete ? (
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={isPending}
          onClick={() => {
            void handleUncomplete()
          }}
        >
          {t('v2.pages.products.detail.rohsFiles.actions.uncomplete')}
        </Button>
      ) : (
        <Button
          type='button'
          size='sm'
          disabled={isPending}
          onClick={() => {
            void handleComplete()
          }}
        >
          {t('v2.pages.products.detail.rohsFiles.actions.complete')}
        </Button>
      )}
    </div>
  )
}
